import { invoiceLineGroups } from './orderLineAmounts';
import { orderItemKey } from './orderItemKeys';
import ExcelJS from 'exceljs/dist/exceljs.min.js';
import { RECEIVE_METHOD_PICKUP_ID } from './adminOrderService';
import { resolveInvoiceSupplier } from './bankAccountInvoiceUtils';
import { amountToUkrainianWords } from './ukrainianAmountInWords';
import RAHUNOK_TEMPLATE_BASE64 from './rahunokInvoiceTemplateBase64';

// Після видалення рядка 15 шаблону (порожній під постачальником) нумерація −1
const ITEM_START_ROW = 21;
const TEMPLATE_ITEM_ROWS = 14; // рядки 21–34
const TEMPLATE_RAZOM_ROW = 36;
const TEMPLATE_PDV_ROW = 37;
const TEMPLATE_RAZOM_PDV_ROW = 38; // другий «Разом» з шаблону — ховаємо
const TEMPLATE_SUMMARY_ROW = 39;
const TEMPLATE_WORDS_ROW = 40;
const TABLE_HEADER_ROW = 19;
const BUYER_VALUE_ROW = 16;
const CLEAR_COL_FROM = 2; // B
const CLEAR_COL_TO = 120; // DP

/** Кур'єр (як у adminOrderService; 1 — відділення / інше — номер відділення). */
const RECEIVE_METHOD_COURIER_ID = 2;

const MONTHS_GENITIVE = [
  'січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
  'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня',
];

export const formatInvoiceDateUa = (createTime, fallbackDate = new Date()) => {
  if (typeof createTime === 'string' && createTime.trim()) {
    const beforeComma = createTime.split(',')[0].trim();
    if (beforeComma) return `${beforeComma} р.`;
  }
  const d = fallbackDate instanceof Date ? fallbackDate : new Date();
  return `${d.getDate()} ${MONTHS_GENITIVE[d.getMonth()]} ${d.getFullYear()} р.`;
};

const isPickupReceiveMethod = (order) => {
  const method = order?.userInfo?.receiveMethod;
  if (!method) return false;
  if (method.id === RECEIVE_METHOD_PICKUP_ID) return true;
  const description = String(method.description || '').toLowerCase();
  return description.includes('самовивіз') || description.includes('самовывоз');
};

const formatDeliveryAddress = (ui) => {
  const receiveMethodId = ui.receiveMethod?.id;
  if (receiveMethodId === RECEIVE_METHOD_COURIER_ID) {
    return [
      ui.street,
      ui.buildingNumber ? `буд. ${ui.buildingNumber}` : '',
      ui.flatNumber ? `кв. ${ui.flatNumber}` : '',
    ].filter(Boolean).join(', ');
  }
  return String(ui.departmentNumber || '').trim();
};

export const buildBuyerFromOrder = (order) => {
  const ui = order?.userInfo || {};
  const fio = [ui.surname, ui.name].filter(Boolean).join(' ').trim();
  const phone = String(ui.phone || '').trim();
  const parts = [];
  if (fio) parts.push(fio);
  if (phone) parts.push(phone);

  if (!isPickupReceiveMethod(order)) {
    const city = String(ui.city || '').trim();
    if (city) parts.push(city);
    const place = formatDeliveryAddress(ui);
    if (place) parts.push(place);
  }

  return parts.join(', ');
};

const collectItems = (orders, { selectedProductIds, selectedOrderItemKeys } = {}) => {
  const list = [];
  (orders || []).forEach((order) => {
    let items = order?.orderItems || [];
    if (selectedOrderItemKeys?.length) {
      items = items.filter((item) => selectedOrderItemKeys.includes(orderItemKey(item)));
    } else if (selectedProductIds?.length) {
      items = items.filter((item) => selectedProductIds.includes(item.productId));
    }
    items.forEach((item) => {
      invoiceLineGroups(item).forEach(group => list.push({
        productCode: item.productCode != null ? String(item.productCode) : '',
        name: item.name || '',
        unit: 'шт',
        ...group,
      }));
    });
  });
  return list;
};

const collectOrderIds = (orders) => (orders || [])
  .map((o) => o.orderId)
  .filter((id) => id != null);

const buildInvoiceNumber = (orderIds) => {
  if (!orderIds.length) return 'б/н';
  // для кількох замовлень у заголовку — лише останній номер
  return String(orderIds[orderIds.length - 1]);
};

const buildPaymentPurpose = (orderIds) => {
  if (!orderIds.length) return 'Оплата за замовлення';
  if (orderIds.length === 1) return `Оплата за замовлення № ${orderIds[0]}`;
  const sorted = orderIds.slice().sort((a, b) => a - b);
  if (sorted.length <= 8) return `Оплата за замовлення № ${sorted.join(', ')}`;
  return `Оплата за замовлення № ${sorted[0]}–${sorted[sorted.length - 1]}`;
};

const resolveInvoiceFileName = (orderIds, fileName) => {
  if (fileName) return fileName;
  if (orderIds.length === 1) return `rahunok-${orderIds[0]}.xlsx`;
  if (orderIds.length > 1 && orderIds.length <= 5) {
    return `rahunok-${orderIds.slice().sort((a, b) => a - b).join('-')}.xlsx`;
  }
  return 'rahunok.xlsx';
};

const formatMoneyUa = (n) => (Number(n) || 0).toLocaleString('uk-UA', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const setCell = (ws, addr, value) => {
  ws.getCell(addr).value = value;
};

const clearRowCols = (ws, row, fromCol = CLEAR_COL_FROM, toCol = CLEAR_COL_TO) => {
  const r = ws.getRow(row);
  for (let c = fromCol; c <= toCol; c += 1) {
    r.getCell(c).value = null;
  }
};

const snapshotMerges = (ws) => Object.values(ws._merges || {}).map((m) => ({ ...m.model }));

/**
 * У шаблоні 1С інколи є «фантомні» рядки біля ліміту Excel (1 048 576).
 * Тоді duplicateRow роздуває rowCount понад ліміт і файл не відкривається.
 */
const compactWorksheetRows = (ws) => {
  const bottom = ws.dimensions?.bottom || ws.actualRowCount || 50;
  const keepUntil = Math.max(bottom + 20, 80);
  if (ws._rows && ws._rows.length > keepUntil) {
    ws._rows.length = keepUntil;
  }
};

/**
 * ExcelJS spliceRows не оновлює merge — перебудовуємо після видалення рядка.
 * snapshots — координати ДО splice; deletedRow — номер видаленого рядка.
 */
const rebuildMergesAfterRowDelete = (ws, snapshots, deletedRow) => {
  ws._merges = Object.create(null);
  snapshots.forEach(({ top, left, bottom, right }) => {
    let r1 = top;
    let r2 = bottom;
    if (r2 < deletedRow) {
      // без змін
    } else if (r1 === deletedRow && r2 === deletedRow) {
      return;
    } else {
      if (r1 >= deletedRow) r1 -= 1;
      if (r2 >= deletedRow) r2 -= 1;
      if (r1 > r2) return;
    }
    try {
      ws.mergeCells(r1, left, r2, right);
    } catch {
      /* overlap */
    }
  });
};

/**
 * ExcelJS duplicateRow/spliceRows не оновлює merge.
 * Перебудовуємо реєстр після вставки count рядків НИЖЧЕ insertAfterRow.
 */
const rebuildMergesAfterInsertBelow = (ws, snapshots, insertAfterRow, count) => {
  ws._merges = Object.create(null);
  snapshots.forEach(({ top, left, bottom, right }) => {
    let r1 = top;
    let r2 = bottom;
    if (r2 <= insertAfterRow) {
      // без змін
    } else if (r1 > insertAfterRow) {
      r1 += count;
      r2 += count;
    } else {
      r2 += count;
    }
    try {
      ws.mergeCells(r1, left, r2, right);
    } catch {
      /* overlap */
    }
  });

  const itemPattern = snapshots.filter(
    (m) => m.top === insertAfterRow && m.bottom === insertAfterRow,
  );
  for (let i = 1; i <= count; i += 1) {
    const row = insertAfterRow + i;
    itemPattern.forEach(({ left, right }) => {
      try {
        ws.mergeCells(row, left, row, right);
      } catch {
        /* overlap */
      }
    });
  }
};

const writeItemRow = (ws, row, item, index) => {
  clearRowCols(ws, row);
  setCell(ws, `B${row}`, index + 1);
  setCell(ws, `D${row}`, item.productCode);
  setCell(ws, `L${row}`, item.name);
  setCell(ws, `CA${row}`, item.quantity);
  setCell(ws, `CG${row}`, item.unit || 'шт');
  setCell(ws, `CQ${row}`, item.price);
  setCell(ws, `DD${row}`, item.sum);
};

const downloadBlob = (buffer, fileName) => {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const loadTemplateWorkbook = async () => {
  const templateBuffer = Uint8Array.from(atob(RAHUNOK_TEMPLATE_BASE64), (c) => c.charCodeAt(0)).buffer;
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(templateBuffer);
  const ws = workbook.worksheets[0];
  if (!ws) throw new Error('У шаблоні немає аркуша');
  compactWorksheetRows(ws);
  return { workbook, ws };
};

/**
 * Заповнює вбудований Excel-шаблон рахунку (без окремого файлу в public/).
 * supplier обовʼязковий — з банківського рахунку або явний override.
 */
export const downloadOrderInvoiceExcel = async ({
  orders,
  buyer,
  supplier,
  invoiceDate,
  selectedProductIds,
  selectedOrderItemKeys,
  fileName,
}) => {
  if (!supplier?.name || !supplier?.edrpou || !supplier?.iban || !supplier?.bank) {
    throw new Error('Не задані реквізити постачальника для рахунку на оплату');
  }

  const orderList = Array.isArray(orders) ? orders : [];
  const orderIds = collectOrderIds(orderList);
  const items = collectItems(orderList, { selectedProductIds, selectedOrderItemKeys });
  const invoiceNo = buildInvoiceNumber(orderIds);
  const purpose = buildPaymentPurpose(orderIds);
  const dateStr = invoiceDate
    || formatInvoiceDateUa(orderList[0]?.createTime)
    || formatInvoiceDateUa(null);
  const buyerText = String(buyer || '').trim() || '—';
  const total = items.reduce((sum, item) => sum + (Number(item.sum) || 0), 0);
  const totalWords = amountToUkrainianWords(total);
  const n = items.length;

  const { workbook, ws } = await loadTemplateWorkbook();

  // Видаляємо порожній рядок 15 — усе нижче зміщується на 1 вгору
  const mergesBeforeDelete = snapshotMerges(ws);
  ws.spliceRows(15, 1);
  rebuildMergesAfterRowDelete(ws, mergesBeforeDelete, 15);
  compactWorksheetRows(ws);

  setCell(ws, 'U3', supplier.name);
  setCell(ws, 'U4', supplier.edrpou);
  setCell(ws, 'J6', supplier.bank);
  setCell(ws, 'BD6', supplier.iban);
  setCell(ws, 'J9', purpose);

  setCell(ws, 'B12', `Рахунок на оплату № ${invoiceNo} від ${dateStr}`);
  setCell(ws, 'H14', supplier.name);
  setCell(ws, `H${BUYER_VALUE_ROW}`, buyerText);

  setCell(ws, `L${TABLE_HEADER_ROW}`, 'Товари');
  setCell(ws, `CQ${TABLE_HEADER_ROW}`, 'Ціна');
  setCell(ws, `DD${TABLE_HEADER_ROW}`, 'Сума');

  let rowShift = 0;
  if (n > TEMPLATE_ITEM_ROWS) {
    const insertAfter = ITEM_START_ROW + TEMPLATE_ITEM_ROWS - 1;
    const count = n - TEMPLATE_ITEM_ROWS;
    const merges = snapshotMerges(ws);
    ws.duplicateRow(insertAfter, count, true);
    rebuildMergesAfterInsertBelow(ws, merges, insertAfter, count);
    rowShift = count;
  }

  const itemRowCount = TEMPLATE_ITEM_ROWS + rowShift;
  for (let i = 0; i < itemRowCount; i += 1) {
    clearRowCols(ws, ITEM_START_ROW + i);
  }
  items.forEach((item, i) => {
    writeItemRow(ws, ITEM_START_ROW + i, item, i);
  });
  for (let i = n; i < TEMPLATE_ITEM_ROWS; i += 1) {
    ws.getRow(ITEM_START_ROW + i).hidden = true;
  }

  const razomRow = TEMPLATE_RAZOM_ROW + rowShift;
  const pdvRow = TEMPLATE_PDV_ROW + rowShift;
  const razomPdvRow = TEMPLATE_RAZOM_PDV_ROW + rowShift;
  const summaryRow = TEMPLATE_SUMMARY_ROW + rowShift;
  const wordsRow = TEMPLATE_WORDS_ROW + rowShift;

  clearRowCols(ws, razomRow);
  clearRowCols(ws, pdvRow);
  clearRowCols(ws, razomPdvRow);
  clearRowCols(ws, summaryRow);
  clearRowCols(ws, wordsRow);

  setCell(ws, `CQ${razomRow}`, 'Разом:');
  setCell(ws, `DD${razomRow}`, total);
  setCell(ws, `DB${pdvRow}`, 'Без ПДВ');
  ws.getRow(razomPdvRow).hidden = true;

  setCell(ws, `B${summaryRow}`, `Усього найменувань ${n}, на суму ${formatMoneyUa(total)} UAH`);
  setCell(ws, `B${wordsRow}`, totalWords);

  const outBuffer = await workbook.xlsx.writeBuffer();
  downloadBlob(outBuffer, resolveInvoiceFileName(orderIds, fileName));
};

export const downloadOrderInvoiceFromOrder = async ({
  order,
  selectedProductIds,
  selectedOrderItemKeys,
  fileName,
  buyer,
  bankAccount,
  supplier,
}) => {
  if (!order) return;
  await downloadOrderInvoiceExcel({
    orders: [order],
    buyer: buyer != null ? buyer : buildBuyerFromOrder(order),
    supplier: resolveInvoiceSupplier({ bankAccount, supplier }),
    selectedProductIds,
  selectedOrderItemKeys,
    fileName,
  });
};

export const downloadOrdersInvoiceExcel = async ({
  orders,
  buyer,
  fileName,
  bankAccount,
  supplier,
}) => {
  const orderList = Array.isArray(orders) ? orders : [];
  let resolvedBuyer = buyer;
  if ((resolvedBuyer == null || String(resolvedBuyer).trim() === '') && orderList.length === 1) {
    resolvedBuyer = buildBuyerFromOrder(orderList[0]);
  }
  await downloadOrderInvoiceExcel({
    orders: orderList,
    buyer: resolvedBuyer,
    supplier: resolveInvoiceSupplier({ bankAccount, supplier }),
    fileName,
  });
};
